/**
 * The NDSS report structure — one manifest, shared by the Results screen and
 * the Scorecard PDF so the two can never drift.
 *
 * The Instant Scan Scorecard mirrors the SECTION STRUCTURE of the full NDSS
 * report (see the full tool's ResultsPage). Sections the Instant Scan genuinely
 * cannot produce at L1 are still shown, clearly marked, with an honest
 * explanation of why and what a Deep Scan would put there.
 *
 * That is the whole point: the gaps are the offer. A visitor should be able to
 * hold this Scorecard next to a full NDSS report and see exactly which parts
 * their own depth of assessment could not fill in.
 */

export type SectionAvailability =
  /** Fully produced at L1. */
  | 'available'
  /** Produced, but materially thinner than the full NDSS. */
  | 'partial'
  /** Cannot exist at L1. Shown as a marked placeholder. */
  | 'unavailable';

export interface ReportSection {
  id: string;
  /** Small label above the title, as in the full NDSS. */
  eyebrow: string;
  /** The full NDSS's own section title, kept verbatim for recognisability. */
  title: string;
  availability: SectionAvailability;
  /** For partial/unavailable: why this depth cannot produce it. */
  why?: string;
  /** For partial/unavailable: what a Deep Scan puts here instead. */
  deepScanAdds?: string;
  /** The depth level at which this section becomes fully available. */
  unlockedAt?: 'L2' | 'L3' | 'L4';
}

export const REPORT_SECTIONS: readonly ReportSection[] = [
  {
    id: 'headline',
    eyebrow: 'Sovereignty score',
    title: 'Your NDSS Score and Posture Level',
    availability: 'available',
  },
  {
    id: 'working-needs-work',
    eyebrow: 'Posture',
    title: "What's working · What needs work",
    availability: 'available',
  },
  {
    id: 'eight-at-a-glance',
    eyebrow: 'Category posture',
    title: 'Eight categories at a glance',
    availability: 'available',
  },
  {
    id: 'assessment-depth',
    eyebrow: 'Assessment depth',
    title: 'How well-supported is the score?',
    availability: 'available',
  },
  {
    id: 'management-summary',
    eyebrow: 'Management summary',
    title: 'What the board needs to know',
    // Produced, but GENERIC: one of five texts keyed to the overall SPL. The UI
    // states this plainly rather than passing it off as tailored analysis.
    availability: 'partial',
    why:
      'This summary is selected from five standard texts, one per posture level. It describes ' +
      'what your level typically means and what usually moves it — it is not written about ' +
      'your organisation, because a self-declared scan has no view of your strategy, your ' +
      'board’s risk appetite or the decisions already in flight.',
    deepScanAdds:
      'A board-level narrative written against your context — your estate, your regulatory ' +
      'position and the work already under way — and reviewed with you before it is issued.',
    unlockedAt: 'L3',
  },
  {
    id: 'inventory',
    eyebrow: 'Inventory in scope',
    title: 'What this score is built on',
    availability: 'unavailable',
    why:
      'The Instant Scan asks about "your main cloud" and "your most critical system" as a ' +
      'single pass. It never collects an inventory, so there are no named providers or ' +
      'workloads to attribute this score to — and no way to tag each one by criticality ' +
      'or by how far it was actually assessed.',
    deepScanAdds:
      'Every provider and workload in scope, named, tagged Critical / Important / Low, ' +
      'each carrying the evidence status actually reached — so the headline is traceable ' +
      'to a concrete, status-tagged inventory rather than a single self-declaration.',
    unlockedAt: 'L2',
  },
  {
    id: 'category-rollup',
    eyebrow: 'Category roll-up',
    title: 'Where the mean rules — and where a ceiling bites',
    availability: 'partial',
    why:
      'Each Instant Scan category rests on one or two questions answered once, so the ' +
      'category score is simply their mean. With no per-provider or per-workload instances ' +
      'there is nothing to roll up, and no critical question can ceiling a category.',
    deepScanAdds:
      'Per-instance answers rolled up per category, showing where the mean governs and ' +
      'where a critical question caps the result — the mechanic that most often explains ' +
      'a score that feels lower than expected.',
    unlockedAt: 'L2',
  },
  {
    id: 'binding-constraints',
    eyebrow: 'Binding constraints',
    title: "What's holding the score back",
    availability: 'unavailable',
    why:
      'Binding constraints come from critical questions ceilinging a category and from red ' +
      'flags observed during assessment. The Instant Scan has neither: no control is marked ' +
      'critical at this depth, and nothing has been observed — only self-declared.',
    deepScanAdds:
      'The specific critical questions capping each category, with the mean-to-capped ' +
      'delta, plus every red flag observed against the control it belongs to.',
    unlockedAt: 'L3',
  },
  {
    id: 'remediation',
    eyebrow: 'Prioritised remediation',
    title: 'What to fix first',
    availability: 'unavailable',
    why:
      'A credible roadmap needs to know what you have already tried, what your architecture ' +
      'will tolerate and what each move would cost you. Ranking fixes by score impact alone ' +
      'would produce advice that looks precise and is not.',
    deepScanAdds:
      'Sequenced actions separated into quick wins and priorities, each with the score ' +
      'movement it would produce and the SPL transition it would unlock.',
    unlockedAt: 'L3',
  },
  {
    id: 'findings',
    eyebrow: 'Findings',
    title: "Observations that don't fit a single answer",
    availability: 'unavailable',
    why:
      'Findings are things a consultant notices that no question asked — an undocumented ' +
      'key-custody model, a drift risk in an estate nobody owns. They come from looking, ' +
      'and the Instant Scan does not look.',
    deepScanAdds:
      'Question-independent observations, each attributed and dated, optionally linked to ' +
      'the controls they touch.',
    unlockedAt: 'L3',
  },
  {
    id: 'methodology',
    eyebrow: 'Method',
    title: 'How this score is built',
    availability: 'available',
  },
] as const;

export const AVAILABLE_SECTIONS = REPORT_SECTIONS.filter((s) => s.availability === 'available');
export const GAP_SECTIONS = REPORT_SECTIONS.filter((s) => s.availability !== 'available');

/** Short summary used on the score screen and in the Scorecard's opening page. */
export function coverageSummary(): string {
  const total = REPORT_SECTIONS.length;
  const available = AVAILABLE_SECTIONS.length;
  return `${available} of ${total} sections of the full NDSS report can be produced at Instant Scan depth.`;
}
